import React, { useEffect, useMemo, useRef, useState } from 'react';
import PanToolIcon from '@mui/icons-material/PanTool';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import './SVGViewer.css';

import { OverlayTrigger, Tooltip as BSTooltip, Spinner } from 'react-bootstrap';


interface SvgFile {
  fileName: string;
  folderPath: string;
}

interface SVGViewerProps {
  svgFiles: SvgFile[];
  mainSvgPath: string;
  mainSvgFileName: string;
  preselectedItemNumber?: number;
}

const MIN_SCALE = 0.02;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.05;

const SVGViewer: React.FC<SVGViewerProps> = ({
  svgFiles,
  mainSvgPath,
  preselectedItemNumber,
}) => {
  //console.log('[INIT] SVGViewer render');

  /** ====== STATE & REFS ====== */
  const [selectedCallouts, setSelectedCallouts] = useState<string[]>([]);
  const selectedRef = useRef<string[]>([]);
  const lastPreselectedRef = useRef<string>('');
  useEffect(() => {
    selectedRef.current = selectedCallouts;
    //console.log('[STATE] selectedCallouts updated =>', selectedCallouts);
  }, [selectedCallouts]);

  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const totalPages = svgFiles?.length ? svgFiles.length : mainSvgPath ? 1 : 0;
  const safePageIndex = Math.min(Math.max(pageIndex, 0), Math.max(totalPages - 1, 0));
  const activeSvgPath = svgFiles?.length
    ? String(svgFiles[safePageIndex]?.folderPath || '').trim()
    : String(mainSvgPath || '').trim();
  const apiBaseUrl = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');

  const resolvedSvgUrl = useMemo(() => {
    const p = activeSvgPath;
    if (!p) return '';

    const preferredProtocol = apiBaseUrl.startsWith('https://')
      ? 'https:'
      : apiBaseUrl.startsWith('http://')
      ? 'http:'
      : '';

    // Absolute URL: keep host/path, align protocol with env when available.
    if (/^https?:\/\//i.test(p)) {
      if (!preferredProtocol) return p;
      try {
        const u = new URL(p);
        u.protocol = preferredProtocol;
        return u.toString();
      } catch {
        return p.replace(/^https?:/i, preferredProtocol);
      }
    }

    // Relative backend media path: anchor to API base URL.
    if (p.startsWith('/')) return apiBaseUrl ? `${apiBaseUrl}${p}` : p;
    if (p.startsWith('media/')) return apiBaseUrl ? `${apiBaseUrl}/${p}` : `/${p}`;

    return apiBaseUrl ? `${apiBaseUrl}/${p.replace(/^\/+/, '')}` : p;
  }, [activeSvgPath, apiBaseUrl]);

  const [panMode, setPanMode] = useState(false);
  const panModeRef = useRef(false);
  const draggedRef = useRef(false);
  const activePointerRef = useRef<number | null>(null);
  const isPanningRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  /** ====== KEY NORMALIZATION (your existing helpers) ====== */
  const norm = (v: unknown) =>
    String(v ?? '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^a-z0-9.]/g, '');

  const numericKey = (s: string) => (/^\d+(\.\d+)?$/.test(s) ? String(Number(s)) : s);
  const keyOf = (raw: any) => numericKey(norm(raw));

  useEffect(() => {
    const key =
      Number.isFinite(preselectedItemNumber as number) && Number(preselectedItemNumber) > 0
        ? String(Number(preselectedItemNumber))
        : '';

    if (!key) {
      lastPreselectedRef.current = '';
      return;
    }

    setSelectedCallouts([key]);

    if (lastPreselectedRef.current !== key) {
      const detail = { itemNumber: key, selected: true };
      window.dispatchEvent(new CustomEvent('svgTextToggle', { detail }));
      window.dispatchEvent(new CustomEvent('bomToggle', { detail }));
      lastPreselectedRef.current = key;
    }
  }, [preselectedItemNumber, svgContent]);

  /** ====== CATALOG → SVG: listen for bomToggle ====== */
  useEffect(() => {
    const onBomToggle = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const key = String(detail.itemNumber ?? '');
      const selected = !!detail.selected;
      //console.log('[BUS] bomToggle IN <=', detail);

      if (!key) return;

      setSelectedCallouts((prev) => {
        const has = prev.includes(key);
        if (selected && !has) {
          //console.log('[BUS] bomToggle APPLY: add', key);
          return [...prev, key];
        }
        if (!selected && has) {
          //console.log('[BUS] bomToggle APPLY: remove', key);
          return prev.filter((x) => x !== key);
        }
        //console.log('[BUS] bomToggle NOOP (already in desired state)', { key, selected, prev });
        return prev;
      });
    };

    window.addEventListener('bomToggle', onBomToggle);
    //console.log('[BUS] bomToggle listener attached');
    return () => {
      window.removeEventListener('bomToggle', onBomToggle);
      //console.log('[BUS] bomToggle listener removed');
    };
  }, []);

  useEffect(() => {
    setPageIndex(0);
  }, [svgFiles, mainSvgPath]);

  useEffect(() => {
    if (safePageIndex !== pageIndex) setPageIndex(safePageIndex);
  }, [safePageIndex, pageIndex]);

  
useEffect(() => {
  const host = svgContainerRef.current;
  if (!host) { console.log('[DOC] host missing at bind time'); }

  const pickCalloutEl = (clientX: number, clientY: number): Element | null => {
    const stack = document.elementsFromPoint(clientX, clientY);
    // log top 6 for visibility
    console.log('[DOC] stack@', clientX, clientY, '→',
      stack.slice(0, 6).map(n => `${n.tagName}${(n as Element).id ? '#'+(n as Element).id : ''}`).join(' → ')
    );

    // prefer obvious candidates
    const cand =
      stack.find(el => el.tagName?.toLowerCase() === 'text') ||
      stack.find(el => el.tagName?.toLowerCase() === 'tspan') ||
      stack.find(el => (el as HTMLElement).dataset?.callout != null) ||
      stack.find(el => /callout|item|pn|balloon|label/i.test(el.id || ''));

    if (cand) return cand;
    return null;
  };

  const onActivateCapture = (ev: Event) => {
    if (panModeRef.current || draggedRef.current) return;
    const t = ev as PointerEvent | MouseEvent;
    const target = t.target as Node | null;
    const container = svgContainerRef.current;

    // 1) Filter: only care about events that occurred inside our SVG container
    const inStage = !!(container && target && container.contains(target));
    if (!inStage) return;

    //console.log('[DOC] activate', t.type, 'target=', (target as Element)?.nodeName);

    // 2) Find a suitable element to derive the callout/key
    const el = pickCalloutEl(t.clientX, t.clientY);
    if (!el) {
      //console.log('[DOC] no callout element found under pointer');
      return;
    }

    // 3) Build a key using your existing normalizer
    const raw =
      (el as HTMLElement).dataset?.callout ||
      (el.textContent || '') ||
      (el.id || '') ||
      (el.getAttribute?.('aria-label') || '');
    const key = keyOf(String(raw));
    //console.log('[DOC] derived key =', key, 'from', el.tagName, (el as any)?.textContent?.trim());

    if (!key) return;

    const isSelected = selectedRef.current.includes(key);
    const willSelect = !isSelected;
    //console.log('[DOC] toggle', { key, isSelected, willSelect });

    setSelectedCallouts(prev =>
      willSelect ? Array.from(new Set([...prev, key])) : prev.filter(x => x !== key)
    );

    const detail = { itemNumber: key, selected: willSelect };
    window.dispatchEvent(new CustomEvent('svgTextToggle', { detail }));
    window.dispatchEvent(new CustomEvent('bomToggle', { detail })); // harmless if unused
    //console.log('[BUS] dispatched svgTextToggle & bomToggle =>', detail);
  };

  console.log('[DOC] binding document capture handlers');
  // capture phase: run before any bubble-phase pan/zoom handlers
  document.addEventListener('pointerup', onActivateCapture, true);
  document.addEventListener('click', onActivateCapture, true);

  return () => {
    //console.log('[DOC] unbinding document capture handlers');
    document.removeEventListener('pointerup', onActivateCapture, true);
    document.removeEventListener('click', onActivateCapture, true);
  };
}, [svgContent]); // re-run after new SVG is injected


useEffect(() => {
  const host = svgContainerRef.current;
  if (!host) { console.log('[PE] host missing'); return; }
  const svg = host.querySelector('svg') as SVGSVGElement | null;
  if (!svg) { console.log('[PE] <svg> NOT found under .svg-stage after svgContent change'); return; }

  (svg as any).style.pointerEvents = 'auto';
  let fixed = 0;

  svg.querySelectorAll('[pointer-events="none"]').forEach((el) => {
    (el as Element).setAttribute('pointer-events', 'auto');
    fixed++;
  });
  svg.querySelectorAll<SVGElement>('[style*="pointer-events"]').forEach((el) => {
    const style = el.getAttribute('style') || '';
    if (/pointer-events\s*:\s*none/i.test(style)) {
      el.setAttribute('style', style.replace(/pointer-events\s*:\s*none;?/gi, 'pointer-events:auto;'));
      fixed++;
    }
  });

//  console.log('[PE] pointer-events fix applied, elements fixed =', fixed);
}, [svgContent]);



  /** ====== FETCH SVG CONTENT ====== */
  useEffect(() => {
    const url = resolvedSvgUrl;
    if (!url) {
      setSvgContent(null);
      return;
    }

    setLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((data) => {
        setSvgContent(data);
      })
      .catch((err) => {
        console.error('[FETCH] SVG fetch error:', err?.message || err);
        setSvgContent(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [resolvedSvgUrl]);

  /** ====== ENSURE POINTER EVENTS AREN'T DISABLED INSIDE SVG ====== */
  useEffect(() => {
    const host = svgContainerRef.current;
    if (!host) { console.log('[PE] host missing'); return; }
    const svg = host.querySelector('svg') as SVGSVGElement | null;
    if (!svg) { console.log('[PE] <svg> NOT found under .svg-stage after svgContent change'); return; }

    (svg as any).style.pointerEvents = 'auto';
    let fixed = 0;

    svg.querySelectorAll('[pointer-events="none"]').forEach((el) => {
      (el as Element).setAttribute('pointer-events', 'auto');
      fixed++;
    });
    svg.querySelectorAll<SVGElement>('[style*="pointer-events"]').forEach((el) => {
      const style = el.getAttribute('style') || '';
      if (/pointer-events\s*:\s*none/i.test(style)) {
        el.setAttribute('style', style.replace(/pointer-events\s*:\s*none;?/gi, 'pointer-events:auto;'));
        fixed++;
      }
    });

    //console.log('[PE] pointer-events fix applied, elements fixed =', fixed);
  }, [svgContent]);

  /** ====== SVG → CATALOG: robust capture-phase handler on <svg> ====== */
  useEffect(() => {
    const host = svgContainerRef.current;
    if (!host) { console.log('[EVT] host missing'); return; }

    const svg = host.querySelector('svg');
    if (!svg) { console.log('[EVT] <svg> NOT found under .svg-stage'); return; }

    const pickCalloutEl = (clientX: number, clientY: number): Element | null => {
      const stack = document.elementsFromPoint(clientX, clientY);
      const cand =
        stack.find(el => el.tagName?.toLowerCase() === 'text') ||
        stack.find(el => el.tagName?.toLowerCase() === 'tspan') ||
        stack.find(el => (el as HTMLElement).dataset?.callout != null) ||
        stack.find(el => /callout|item|pn|balloon|label/i.test(el.id || ''));

      if (cand) return cand;
      return null;
    };

    const keyFromEl = (el: Element): string => {
      const raw =
        (el as HTMLElement).dataset?.callout ||
        (el.textContent || '') ||
        (el.id || '') ||
        (el.getAttribute?.('aria-label') || '');
      const k = keyOf(String(raw));
      return k;
    };

    const onActivate = (ev: Event) => {
      if (panModeRef.current || draggedRef.current) return;
      const t = ev as PointerEvent | MouseEvent;
      const target = t.target as Element | null;

      console.log('[EVT] activate', t.type, 'target=', target?.tagName);

      const el = pickCalloutEl(t.clientX, t.clientY);
      if (!el) {
        // explain what we actually hit
        const stack = document.elementsFromPoint(t.clientX, t.clientY);
        const chain = stack.slice(0, 6).map(n => `${n.tagName}${(n as Element).id ? '#' + (n as Element).id : ''}`);
        console.log('[EVT] no callout element found. Stack top→bottom:', chain.join(' → '));
        return;
      }

      const key = keyFromEl(el);
      console.log('[EVT] derived key =', key, 'from element', el.tagName, (el as any)?.textContent?.trim());
      if (!key) return;

      const isSelected = selectedRef.current.includes(key);
      const willSelect = !isSelected;
      console.log('[EVT] toggle', { key, isSelected, willSelect });

      setSelectedCallouts(prev =>
        willSelect ? Array.from(new Set([...prev, key])) : prev.filter(x => x !== key)
      );

      const detail = { itemNumber: key, selected: willSelect };
      window.dispatchEvent(new CustomEvent('svgTextToggle', { detail }));
      window.dispatchEvent(new CustomEvent('bomToggle', { detail })); // safe if unused
      console.log('[BUS] dispatched svgTextToggle & bomToggle =>', detail);
    };

    console.log('[EVT] bind capture handlers on <svg>');
    svg.addEventListener('pointerup', onActivate, true);
    svg.addEventListener('click', onActivate, true);

    return () => {
      console.log('[EVT] unbind capture handlers from <svg>');
      svg.removeEventListener('pointerup', onActivate, true);
      svg.removeEventListener('click', onActivate, true);
    };
  }, [svgContent]);

  /** ====== HIGHLIGHTS: apply .selected-callout on <text> ====== */
  useEffect(() => {
    const host = svgContainerRef.current;
    if (!host) { console.log('[HL] host missing'); return; }

    const apply = () => {
      const svgNode = host.querySelector('svg');
      if (!svgNode) { console.log('[HL] no <svg> to apply'); return; }

      const textNodes = svgNode.querySelectorAll('text');
      let hits = 0;

      textNodes.forEach((node) => {
        const key = keyOf(node.textContent || '');
        if (!key) return;
        const on = selectedCallouts.includes(key);
        node.classList.toggle('selected-callout', on);
        const target = (node.closest('g') as SVGElement) || (node as SVGElement);
        if (on) {
          hits++;
        } 
      });

      //console.log('[HL] applied highlight. keys=', selectedCallouts, 'hits=', hits);
    };

    // immediate apply
    apply();

    // re-apply on DOM changes (some pan/zoom lib rebuild nodes)
    const obs = new MutationObserver(() => requestAnimationFrame(apply));
    obs.observe(host, { subtree: true, childList: true, attributes: true });

    return () => obs.disconnect();
  }, [selectedCallouts, svgContent]);

  /** ====== PAN & ZOOM ====== */
  const zoomAtPoint = (delta: number, clientX: number, clientY: number) => {
    const zoomHost = canvasRef.current || viewportRef.current;
    if (!zoomHost) return;
    const rect = zoomHost.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;

    const prevScale = scale;
    const nextScale = Math.max(
      MIN_SCALE,
      Math.min(MAX_SCALE, prevScale * (delta > 0 ? 1 - ZOOM_STEP : 1 + ZOOM_STEP))
    );

    const dx = mx - tx;
    const dy = my - ty;
    const factor = nextScale / prevScale;

    const nextTx = mx - dx * factor;
    const nextTy = my - dy * factor;

    console.log('[ZOOM] from', { scale: prevScale, tx, ty }, 'to', { scale: nextScale, tx: nextTx, ty: nextTy });

    setTx(nextTx);
    setTy(nextTy);
    setScale(nextScale);

    setLoading(true);
    setTimeout(() => setLoading(false), 150);
  };

  const zoomAtCenter = (delta: number) => {
    const host = canvasRef.current || viewportRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    zoomAtPoint(delta, rect.left + host.clientWidth / 2, rect.top + host.clientHeight / 2);
  };

  const zoomIn = () => zoomAtCenter(-1);

  const zoomOut = () => zoomAtCenter(+1);

  const normalizeSvgSize = (svg: SVGSVGElement) => {
    svg.style.width = 'auto';
    svg.style.height = 'auto';
    svg.style.maxWidth = 'none';
    svg.style.maxHeight = 'none';
    svg.style.display = 'block';
    svg.style.overflow = 'visible';
  };

  const resetView = () => {
    fitView();
  };

  const fitView = () => {
    const canvas = canvasRef.current;
    const host = svgContainerRef.current;
    const svg = host?.querySelector('svg') as SVGSVGElement | null;
    if (!canvas || !svg) return;

    normalizeSvgSize(svg);

    let contentX = 0;
    let contentY = 0;
    let contentW = 0;
    let contentH = 0;

    const vb = svg.viewBox?.baseVal;
    if (vb && vb.width > 0 && vb.height > 0) {
      // Prefer viewBox: this represents the full SVG page area.
      contentX = vb.x || 0;
      contentY = vb.y || 0;
      contentW = vb.width;
      contentH = vb.height;
    }

    if (contentW <= 0 || contentH <= 0) {
      try {
        const box = svg.getBBox();
        if (box.width > 0 && box.height > 0) {
          contentX = box.x;
          contentY = box.y;
          contentW = box.width;
          contentH = box.height;
        }
      } catch {
        // ignore and continue to attribute/client fallback
      }
    }

    if (contentW <= 0 || contentH <= 0) {
      const widthAttr = parseFloat(svg.getAttribute('width') || '');
      const heightAttr = parseFloat(svg.getAttribute('height') || '');
      const w = Number.isFinite(widthAttr) && widthAttr > 0 ? widthAttr : svg.clientWidth;
      const h = Number.isFinite(heightAttr) && heightAttr > 0 ? heightAttr : svg.clientHeight;
      contentW = w > 0 ? w : 1000;
      contentH = h > 0 ? h : 800;
    }

    const padding = 16;
    const availW = Math.max(0, canvas.clientWidth - padding * 2);
    const availH = Math.max(0, canvas.clientHeight - padding * 2);
    if (!availW || !availH || !contentW || !contentH) return;

    const rawFitScale = Math.min(availW / contentW, availH / contentH);
    if (!Number.isFinite(rawFitScale) || rawFitScale <= 0) return;

    // Fit must be able to zoom out below normal manual minimum when SVG is very large.
    const fitScale = Math.max(0.001, Math.min(1, rawFitScale * 0.90));
    const nextTx = (canvas.clientWidth - contentW * fitScale) / 2 - contentX * fitScale;
    const nextTy = (canvas.clientHeight - contentH * fitScale) / 2 - contentY * fitScale;

    setScale(fitScale);
    setTx(nextTx);
    setTy(nextTy);
  };

  useEffect(() => {
    if (!svgContent) return;

    const id = requestAnimationFrame(() => {
      fitView();
    });

    return () => cancelAnimationFrame(id);
  }, [svgContent, safePageIndex]);

  const goToPage = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= totalPages) return;
    setPageIndex(nextIndex);
  };

  const goPrevPage = () => goToPage(safePageIndex - 1);
  const goNextPage = () => goToPage(safePageIndex + 1);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as Element).closest('.viewer-hud')) return;
    if (!e.isPrimary || e.button !== 0) return;
    draggedRef.current = false;
    if (e.pointerType !== 'mouse' && !panModeRef.current) return;
    activePointerRef.current = e.pointerId;
    isPanningRef.current = true;
    panStartRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== activePointerRef.current || !panStartRef.current) return;
    const { x, y, tx: startTx, ty: startTy } = panStartRef.current;
    const dx = e.clientX - x;
    const dy = e.clientY - y;
    if (Math.hypot(dx, dy) > 4) draggedRef.current = true;
    if (!draggedRef.current) return;
    setTx(startTx + dx);
    setTy(startTy + dy);
  };

  const endPan = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== activePointerRef.current) return;
    activePointerRef.current = null;
    isPanningRef.current = false;
    panStartRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };
  const prettyPercent = Math.round(scale * 100);
  const hasSvgSource = !!activeSvgPath;

  // return (
  //   <Box className="svgviewer-root" sx={{ position: 'relative', width: '100%'}}>
  //     {/* Loader Spinner */}
  //     {loading && (
  //       <div
  //         style={{
  //           position: 'absolute',
  //           left: '50%',
  //           transform: 'translateX(-50%)',
  //           zIndex: 9999,
  //           background: 'rgba(255,255,255,0.8)',
  //           padding: '4px 12px',
  //           borderRadius: '6px',
  //           display: 'flex',
  //           alignItems: 'center',
  //           // gap: '8px',
  //         }}
  //       >
  //         <CircularProgress size={16} />
  //         <span style={{ fontSize: '0.8rem' }}>Refreshing View…</span>
  //       </div>
  //     )}

  //     {svgContent ? (
  //       <div
  //         ref={viewportRef}
  //         className="svg-viewport"
  //         onMouseDown={onMouseDown}
  //         onMouseMove={onMouseMove}
  //         onMouseUp={endPan}
  //         onMouseLeave={endPan}
  //         onDoubleClick={zoomIn}
  //         onDragStart={(e) => e.preventDefault()} 
  //         style={{ overflow: 'hidden', position: 'relative', width: '100%', height: '100%' }}
  //       >
  //         {/* HUD Controls */}
  //         <div className="viewer-hud">
  //           <span className="hud-title">{mainSvgFileName || 'SVG Viewer'}</span>
  //           <span className="hud-divider" />
  //           <span className="zoom-readout">{prettyPercent}%</span>
  //           <div className="hud-tools">
            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Pan: drag with one finger</BSTooltip>}>
              <button
                className="svg-btn svg-btn--icon"
                aria-label="Pan"
                aria-pressed={panMode}
                onClick={() => {
                  panModeRef.current = !panModeRef.current;
                  setPanMode(panModeRef.current);
                  draggedRef.current = false;
                }}
                type="button"
              >
                <PanToolIcon fontSize="small" />
              </button>
            </OverlayTrigger>
  //             <Tooltip title="Zoom Out">
  //               <IconButton size="small" onClick={zoomOut}>
  //                 <RemoveIcon fontSize="small" />
  //               </IconButton>
  //             </Tooltip>
  //             <Tooltip title="Zoom In">
  //               <IconButton size="small" onClick={zoomIn}>
  //                 <AddIcon fontSize="small" />
  //               </IconButton>
  //             </Tooltip>
  //             <Tooltip title="Reset">
  //               <IconButton size="small" onClick={resetView}>
  //                 <RestartAltIcon fontSize="small" />
  //               </IconButton>
  //             </Tooltip>
  //             <Tooltip title="Fit">
  //               <IconButton size="small" onClick={resetView}>
  //                 <FitScreenIcon fontSize="small" />
  //               </IconButton>
  //             </Tooltip>
  //           </div>
  //         </div>

  //         {/* Pan/Zoom Wrapper */}
  //         <div
  //           style={{
  //             transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
  //             transformOrigin: '0 0',
  //             position: 'relative',
  //             width: '100%',
  //             height: '100%',
  //           }}
  //         >
  //           {/* Base SVG injected into DOM */}
  //           <div
  //             ref={svgContainerRef}
  //             className="svg-stage"
  //             dangerouslySetInnerHTML={{ __html: svgContent }}
  //             style={{ pointerEvents: 'auto' }}
  //           />
  //         </div>
  //       </div>
  //     ) : (
  //       <div className="svg-viewport loading-state">Loading...</div>
  //     )}
  //   </Box>
  // );
return (
  <div className="svgviewer-root" style={{ position: 'relative', width: '100%' }}>
    {/* Loader Spinner */}
    {loading && (
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: 'rgba(255,255,255,0.8)',
          padding: '4px 12px',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Spinner animation="border" size="sm" />
        <span style={{ fontSize: '0.8rem' }}>Refreshing View…</span>
      </div>
    )}

    {svgContent ? (
      <div
        ref={viewportRef}
        className="svg-viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onLostPointerCapture={endPan}
        onDoubleClick={zoomIn}
        onDragStart={(e) => e.preventDefault()}
        style={{ overflow: 'hidden', position: 'relative', width: '100%', height: '100%' }}
      >
        {/* HUD Controls */}
        <div className="viewer-hud">
          <div className="hud-page-nav">
            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Previous Page</BSTooltip>}>
              <button
                className="svg-btn svg-btn--icon"
                onClick={goPrevPage}
                disabled={safePageIndex <= 0}
                type="button"
              >
                <NavigateBeforeIcon fontSize="small" />
              </button>
            </OverlayTrigger>
            <span className="hud-page-indicator">
              Page {totalPages ? safePageIndex + 1 : 0} of {totalPages || 0}
            </span>
            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Next Page</BSTooltip>}>
              <button
                className="svg-btn svg-btn--icon"
                onClick={goNextPage}
                disabled={safePageIndex >= totalPages - 1}
                type="button"
              >
                <NavigateNextIcon fontSize="small" />
              </button>
            </OverlayTrigger>
          </div>

          <div className="hud-spacer" />
          <span className="zoom-readout">{prettyPercent}%</span>
          <div className="hud-tools">
            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Pan: drag with one finger</BSTooltip>}>
              <button
                className="svg-btn svg-btn--icon"
                aria-label="Pan"
                aria-pressed={panMode}
                onClick={() => {
                  panModeRef.current = !panModeRef.current;
                  setPanMode(panModeRef.current);
                  draggedRef.current = false;
                }}
                type="button"
              >
                <PanToolIcon fontSize="small" />
              </button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Zoom Out</BSTooltip>}>
              <button className="svg-btn svg-btn--icon" onClick={zoomOut} type="button">
                <RemoveIcon fontSize="small" />
              </button>
            </OverlayTrigger>

            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Zoom In</BSTooltip>}>
              <button className="svg-btn svg-btn--icon" onClick={zoomIn} type="button">
                <AddIcon fontSize="small" />
              </button>
            </OverlayTrigger>

            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Reset</BSTooltip>}>
              <button className="svg-btn svg-btn--icon" onClick={resetView} type="button">
                <RestartAltIcon fontSize="small" />
              </button>
            </OverlayTrigger>

            <OverlayTrigger placement="bottom" overlay={<BSTooltip>Fit</BSTooltip>}>
              <button className="svg-btn svg-btn--icon" onClick={fitView} type="button">
                <FitScreenIcon fontSize="small" />
              </button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Pan/Zoom Wrapper */}
        <div ref={canvasRef} className="svg-canvas">
          <div
            className="svg-panzoom-layer"
            style={{
              transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
              transformOrigin: '0 0',
            }}
          >
            {/* Base SVG injected into DOM */}
            <div
              ref={svgContainerRef}
              className="svg-stage"
              dangerouslySetInnerHTML={{ __html: svgContent }}
              style={{ pointerEvents: 'auto' }}
            />
          </div>
        </div>
      </div>
    ) : (
      <div className="svg-viewport loading-state d-flex justify-content-center align-items-center">
        {hasSvgSource ? 'Loading...' : 'No SVG data available'}
      </div>
    )}
  </div>
);


};

export default SVGViewer;
